import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import FormCaptcha from '@s-ui/react-form-captcha'

import ComplaintFormConfigField from './components/ComplaintFormConfigField.js'
import ComplaintTypeField from './components/ComplaintTypeField.js'
import {useTranslateForm} from './utils/useTranslateForm.js'
import {isFieldValueValid} from './utils/validateComplaintField.js'
import {BASE_CLASS, COMPLAINT_FORM_ID} from './config.js'

const ComplaintForm = ({adId, complaintTypeId, isProfessional, onChangeComplaintType, onError, onSubmit}) => {
  const {domain, i18n} = useContext(Context)
  const {translateForm} = useTranslateForm()

  const [formFields, setFormFields] = useState(null)
  const [fieldValues, setFieldValues] = useState({})
  const [captchaToken, setCaptchaToken] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const config = domain.get('config')
  const {COMPLAINT_TYPE_IDS} = config.get('report')
  const isAlreadySoldPrivate = complaintTypeId === COMPLAINT_TYPE_IDS.alreadySoldPrivate

  useEffect(() => {
    setHasAttemptedSubmit(false)

    if (!complaintTypeId) {
      setFormFields(null)
      setFieldValues({})
      return
    }

    let isCurrent = true

    domain
      .get('get_ad_complaint_config_form_use_case')
      .execute({complaintTypeId})
      .then(([error, configForm]) => {
        if (error || !isCurrent) return

        const {form, initialFieldValues} = configForm
        translateForm(form)
        setFormFields(form.fields)
        setFieldValues(initialFieldValues)
      })

    return () => {
      isCurrent = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complaintTypeId])

  const handleFieldChange = (fieldId, value) => {
    setFieldValues(previousValues => ({...previousValues, [fieldId]: value}))
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (isSubmitting) return

    setHasAttemptedSubmit(true)

    // Required-field completeness surfaces as a per-field Field.Error (see ComplaintFormConfigField and
    // ComplaintTypeField above); the reCAPTCHA gate has no per-field UI of its own, so it just blocks
    // silently until solved.
    if (!complaintTypeId || !isFormValid || (isAlreadySoldPrivate && !captchaToken)) return

    setIsSubmitting(true)

    const [error] = await domain
      .get('send_complaint_ads_use_case')
      .execute({adId, captchaToken, complaintTypeId, fields: fieldValues})

    setIsSubmitting(false)

    if (error) {
      onError()
      return
    }

    onSubmit()
  }

  const isFormValid = Boolean(formFields && formFields.every(field => isFieldValueValid(field, fieldValues[field.id])))

  return (
    <form className={BASE_CLASS} id={COMPLAINT_FORM_ID} noValidate onSubmit={handleSubmit}>
      <ComplaintTypeField
        complaintTypeId={complaintTypeId}
        hasAttemptedSubmit={hasAttemptedSubmit}
        isProfessional={isProfessional}
        onChange={onChangeComplaintType}
        className={`${BASE_CLASS}--typeField`}
      />

      {formFields &&
        formFields.map(field => (
          <ComplaintFormConfigField
            key={field.id}
            field={field}
            hasAttemptedSubmit={hasAttemptedSubmit}
            onChange={value => handleFieldChange(field.id, value)}
            value={fieldValues[field.id]}
            className={`${BASE_CLASS}--configField`}
          />
        ))}

      {isAlreadySoldPrivate && (
        <FormCaptcha locale={i18n.locale} onSubmit={setCaptchaToken} siteKey={config.get('RE_CAPTCHA_KEY')} />
      )}
    </form>
  )
}

ComplaintForm.displayName = 'ComplaintForm'

ComplaintForm.propTypes = {
  adId: PropTypes.string.isRequired,
  complaintTypeId: PropTypes.string,
  isProfessional: PropTypes.bool,
  onChangeComplaintType: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

export default ComplaintForm
